"use client"

import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * The Graphite form field, from canvas 14e: "fields are underlines, not boxes —
 * the form keeps its reading rhythm and the only lit underline is the one you
 * are in."
 *
 * It lives here rather than beside any one form because editing a candidate and
 * creating a placement draw the same field, and a boxed input beside an
 * underlined one reads as two different products.
 */

export const FIELD_LABEL =
  "text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground"

/**
 * The underline itself. Exported so a control this file does not own — a
 * select trigger, a date button, an input driven by react-hook-form's
 * `register` — can wear it without being wrapped in a controlled component.
 */
// `ring-offset-0` as well as `ring-0`: the base input's focus offset is a 2px
// shadow in the page's colour, and on its own it drew a square box around an
// underline — a field that is meant to have no box at all.
export const FIELD_UNDERLINE =
  "w-full rounded-none border-0 border-b bg-transparent px-0 text-[13.5px] leading-[1.5] shadow-none outline-none transition-colors focus:border-primary focus-visible:ring-0 focus-visible:ring-offset-0"

/** A label over an arbitrary control, on the same baseline as `Field`. */
export function FieldShell({
  label,
  children,
  className,
}: {
  /** A node rather than a string, so a required marker can sit in the label. */
  label: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className={FIELD_LABEL}>{label}</div>
      <div className="mt-[9px]">{children}</div>
    </div>
  )
}

/** Mono, for machine values — money, percentages, dates already formatted. */
export const FIELD_MONO = "font-mono text-[13px] leading-[1.4]"

/**
 * A short, closed set of choices written as words on the field's underline,
 * with the chosen one carrying a short brand rule beneath it — canvas 15a's
 * placement type and 15c's event type. A dropdown would hide two of three
 * options behind a click for no gain.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  label: string
  options: readonly T[]
  value: T
  onChange: (value: T) => void
  className?: string
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className={FIELD_LABEL}>{label}</div>
      <div
        role="radiogroup"
        aria-label={label}
        className="mt-[11px] flex flex-wrap gap-x-5 border-b pb-[7px]"
      >
        {options.map((option) => {
          const selected = option === value
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option)}
              className={cn(
                "whitespace-nowrap text-[12.5px] leading-none transition-colors",
                selected
                  ? "font-medium text-foreground shadow-[0_7px_0_-6px_var(--primary)]"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** A controlled underline field. */
export function Field({
  label,
  value,
  onChange,
  multiline,
  readOnly,
  placeholder,
  className,
  controlClassName,
  onKeyDown,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  multiline?: boolean
  readOnly?: boolean
  placeholder?: string
  className?: string
  /** Extra classes for the control — container-query type sizing, mostly. */
  controlClassName?: string
  /** For forms that submit on Enter. Never wired by default: in a textarea
      Enter is a newline, so it is the caller who knows which is which. */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
}) {
  const shared = cn(FIELD_UNDERLINE, "mt-[9px]", controlClassName)

  return (
    <div className={cn("min-w-0", className)}>
      <div className={FIELD_LABEL}>{label}</div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={label}
          rows={3}
          readOnly={readOnly}
          placeholder={placeholder}
          className={cn(
            shared,
            "resize-none pb-2.5 leading-[1.7]",
            readOnly && "cursor-not-allowed text-muted-foreground",
          )}
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={label}
          readOnly={readOnly}
          placeholder={placeholder}
          className={cn(
            shared,
            "pb-[7px]",
            readOnly && "cursor-not-allowed text-muted-foreground",
          )}
        />
      )}
    </div>
  )
}
